// libraries
import type { RetentionDecisions } from '@makemydeal/dr-activities-change-vehicle';
import {
    actionCreators as offerReduxActionCreators,
    selectors as offerReduxSelectors,
    utils as offerReduxUtils
} from '@makemydeal/dr-offer-redux';

// interfaces/types
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ManualIncentiveEntry } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import type { DashStoreState, DashStoreThunk } from '../types/appState';

// action creators
import { retainManualIncentives, updateManualIncentives } from './offerActionCreators';
import { selectedConditionalIncentives } from './offerRedux';
import { retainVppProducts, unselectAllVppProducts } from './vehicleProtectionActionCreators';
import { retainOverriddenTaxes } from './taxesActionCreators';
import { addManualAccessories } from './accessoriesActionCreators';
import { clearTestDrive } from './testDriveActionCreators';
import { clearReservation } from './reservationActionCreators';

// selectors
import { getSelectedManualAccessories } from '../selectors/accessoriesSelectors';
import * as pendingDealerReviewSelectors from '../selectors/pendingDealerReviewSelectors';

const isProgramSourced = ({ manual }: ManualIncentiveEntry): boolean => manual === false;

const isUserEntered = (incentive: ManualIncentiveEntry): boolean => !isProgramSourced(incentive);

const getUserEnteredManualIncentives = (state: any): ManualIncentiveEntry[] =>
    (offerReduxSelectors.getManualIncentives(state) || []).filter(isUserEntered);

// Payload that keeps only the dealer's user-entered MANUAL incentives (dropping program-derived ones) with
// the running dealerCash/rebate totals. Shared by the switch reset (restoration-on branch) and the AVO
// new-scenario action so the two can't drift.
const buildUserManualIncentivesPayload = (state: any) => {
    const userManualIncentives = getUserEnteredManualIncentives(state);
    return {
        manualIncentives: userManualIncentives,
        dealerCashTotal: offerReduxUtils.incentives.getIncentiveSum(
            userManualIncentives.filter(offerReduxUtils.incentives.isDealerCash)
        ),
        totalRebates: offerReduxUtils.incentives.getIncentiveSum(
            userManualIncentives.filter(offerReduxUtils.incentives.isNotDealerCash)
        ),
        skipPayment: true
    };
};

// The switch-without-a-decision path: the retention modal was never shown, so there is nothing to
// honour. When program-incentive restoration is enabled the dealer's own rebates stay on the deal and
// only program-derived incentives are dropped; otherwise all manual incentives are cleared. What the
// dealer keeps when the modal *is* shown is decided by getManualIncentivesToRetain instead.
const buildManualIncentivesResetPayload = (state: any) =>
    offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)
        ? buildUserManualIncentivesPayload(state)
        : { manualIncentives: [], dealerCashTotal: 0, totalRebates: 0, skipPayment: true };

// Re-derive incentives per vehicle for a new AVO scenario: keep only the dealer's user-entered MANUAL
// incentives and drop program-derived ones (tied to the source vehicle's eligibility). Independent of the
// enableProgramIncentivesRestoration_F292564 toggle — unlike buildManualIncentivesResetPayload — because the
// AVO card always sends the manual-only total for an alternate, so the scenario must match. Returns a plain
// action so the caller can scope it to the new scope.
export const buildManualOnlyIncentivesAction = (state: any): AnyFSA =>
    updateManualIncentives(buildUserManualIncentivesPayload(state));

// True when the deal carries a program-derived (non-user-entered) incentive a new AVO scenario must drop.
export const hasProgramSourcedIncentive = (state: any): boolean =>
    (offerReduxSelectors.getManualIncentives(state) || []).some(isProgramSourced);

/** Kept program conditionals. `getIncentivesForRetentionModal` never offers a standard, so this re-states
 * the same rule rather than relying on the modal's list. */
const getRetainedProgramConditionalIds = (retained: RetentionDecisions): number[] =>
    retained.incentives
        .filter((incentive) => isProgramSourced(incentive) && !offerReduxUtils.incentives.isProgramStandardIncentive(incentive))
        .map(({ code }) => offerReduxUtils.incentives.toValidIncentiveCode(code))
        .filter(offerReduxUtils.incentives.isDefinedIncentiveCode);

const buildRetainedProgramSelectionKeys = (state: any, retained: RetentionDecisions): string[] | undefined => {
    if (!offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)) return undefined;
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    return offerReduxUtils.incentives.buildConditionalSelectionKeys(offerType, getRetainedProgramConditionalIds(retained), true);
};

// The modal's checkboxes decide what carries over. Program rows are left out under
// restoration - they come back through the recorded conditional selection, not the manual list; with
// restoration off there is no such recording, so writing them here is what keeps them.
const getManualIncentivesToRetain = (state: any, retained: RetentionDecisions): ManualIncentiveEntry[] =>
    offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)
        ? retained.incentives.filter(isUserEntered)
        : retained.incentives;

export const countIncentivesRetainedOnSwitch = (state: any, retained: RetentionDecisions): number =>
    offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)
        ? getManualIncentivesToRetain(state, retained).length + getRetainedProgramConditionalIds(retained).length
        : retained.incentives.length;

export const applyVehicleSwitchRetention =
    (retained: RetentionDecisions): DashStoreThunk =>
    (dispatch, getState) => {
        const state = getState();
        const retainedSelectionKeys = buildRetainedProgramSelectionKeys(state, retained);
        dispatch(retainManualIncentives(getManualIncentivesToRetain(state, retained)));
        if (retainedSelectionKeys) {
            if (
                offerReduxUtils.incentives.isVehicleSwitchStandardsHandoffEnabled(state) &&
                offerReduxSelectors.getCurrentOfferType(state) !== CASH &&
                !pendingDealerReviewSelectors.isPendingDealerReview(state)
            ) {
                dispatch(selectedConditionalIncentives(retainedSelectionKeys, true, false, true));
            } else {
                dispatch(selectedConditionalIncentives(retainedSelectionKeys, true, false));
            }
        }
        dispatch(retainVppProducts(retained.vpp));
        dispatch(addManualAccessories(retained.accessories, { skipPayment: true }));
        dispatch(retainOverriddenTaxes(retained.taxes));
    };

const shouldReleaseReservation = (state: DashStoreState) => {
    const { reservation } = state;

    if (reservation?.isReservedByThisShopper === false) {
        return false;
    }

    return (reservation?.amount ?? 0) === 0;
};

export const clearAppointmentDataForVehicleSwitch = (): DashStoreThunk => (dispatch, getState) => {
    dispatch(clearTestDrive());
    dispatch(offerReduxActionCreators.clearVehicleFulfillment());

    if (shouldReleaseReservation(getState())) {
        dispatch(clearReservation());
    }
};

export const clearItemsForVehicleSwitch = (): DashStoreThunk => (dispatch, getState) => {
    const state = getState();
    dispatch(updateManualIncentives(buildManualIncentivesResetPayload(state)));
    dispatch(unselectAllVppProducts({ skipPayment: true }));
    const selectedManualAccessories = getSelectedManualAccessories(state);
    dispatch(addManualAccessories(selectedManualAccessories, { skipPayment: true }));
};
